// Admin Courses — two-part flow:
//   Part 1 "Add Course": course metadata only (no video).
//   Part 2 "Lessons": per course, add multiple lessons — each lesson is either an
//           uploaded video file OR a web/YouTube URL.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { chunkedUpload } from '@/lib/chunkedUpload';
import { useTaxonomies } from '@/lib/useTaxonomies';
import { toast } from 'sonner';
import {
    Loader2, Trash2, Edit3, Eye, ThumbsUp, Star, Video as VideoIcon,
    Plus, ListVideo, X, Youtube, Link as LinkIcon, Upload, CheckCircle2, Settings2, Filter, Search, ChevronDown, Check,
} from 'lucide-react';

const LANGS = ['en', 'hi', 'ta', 'ml', 'te', 'kn', 'bn', 'or', 'mr', 'gu'];
const inputCls = 'w-full px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:ring-2 focus:ring-amber-400 focus:outline-none text-sm bg-white';

export default function AdminVideos() {
    const tax = useTaxonomies();
    // Add a brand-new persona on the fly (saved to the persona taxonomy, then re-fetched).
    const addPersona = async (value) => {
        try {
            await api.post('/admin/taxonomies', { type: 'persona', value, label: value });
            await tax.refresh();
            toast.success(`Persona "${value}" added`);
        } catch (e) {
            // 409 = already exists → still selectable, no error needed
            if (e?.response?.status !== 409) toast.error('Could not add persona');
        }
    };
    // Add a brand-new course category on the fly (saved to the category taxonomy, re-fetched).
    const addCategory = async (label) => {
        const slug = label.trim().toLowerCase().replace(/\s+/g, '_');
        try {
            await api.post('/admin/taxonomies', { type: 'category', value: slug, label: label.trim() });
            await tax.refresh();
            toast.success(`Category "${label.trim()}" added`);
        } catch (e) {
            const dup = e?.response?.status === 409 || /already exists/i.test(e?.response?.data?.detail || '');
            if (!dup) toast.error('Could not add category');
        }
        return slug;
    };
    // Remove an existing course category from the dropdown (taxonomy delete; existing
    // courses keep their stored value). Re-addable any time via "+ Add".
    const removeCategory = async (opt) => {
        const remaining = (tax.category || []).filter((c) => c.id !== opt.id);
        try {
            await api.delete(`/admin/taxonomies/${opt.id}`);
            await tax.refresh();
            toast.success(`Category "${opt.label}" removed`);
            if (createCategory === opt.value) setCreateCategory(remaining[0]?.value || '');
            if (editCategory === opt.value) setEditCategory(remaining[0]?.value || '');
        } catch (e) {
            toast.error('Could not remove category');
        }
    };
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [editPersonas, setEditPersonas] = useState(['all']);
    const [thumbBusy, setThumbBusy] = useState(false);
    const [editCategory, setEditCategory] = useState('');
    const [importOpen, setImportOpen] = useState(false);
    const [importBusy, setImportBusy] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [createPersonas, setCreatePersonas] = useState(['all']);
    const [createCategory, setCreateCategory] = useState('');
    const [lessonsCourse, setLessonsCourse] = useState(null);
    const [flt, setFlt] = useState({ q: '', category: '', language: '', region: '', zone: '', market: '', persona: '', published: '' });
    const [creating, setCreating] = useState(false);
    const [courseMode, setCourseMode] = useState('new');   // 'new' | 'link'
    const [linkCourseId, setLinkCourseId] = useState('');   // existing course this is a language version of
    const [editLinkId, setEditLinkId] = useState('');       // re-link target in the Edit modal
    const [linkWizard, setLinkWizard] = useState(false);    // retro-link wizard modal
    const [groupView, setGroupView] = useState(false);      // group course list by Course ID

    // Default the create-form category to the first available option once taxonomies load.
    useEffect(() => {
        if (!createCategory && (tax.category || []).length) setCreateCategory(tax.category[0].value);
    }, [tax.category, createCategory]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/videos');
            setVideos(r.data || []);
        } catch {
            toast.error('Failed to load courses');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onCreateCourse = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const fd = new FormData(form);
        if (!String(fd.get('title') || '').trim()) { toast.error('Title is required'); return; }
        if (courseMode === 'link') {
            if (!linkCourseId) { toast.error('Pick the existing course this is another language version of'); return; }
            fd.set('link_course_id', linkCourseId);
        }
        fd.set('personas', (createPersonas.length ? createPersonas : ['all']).join(','));
        setCreating(true);
        try {
            await api.post('/admin/videos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(courseMode === 'link' ? 'Language version added under the same Course ID' : 'Course added — now add lessons to it');
            form.reset();
            setCreatePersonas(['all']);
            setCreateCategory((tax.category || [])[0]?.value || '');
            setCourseMode('new');
            setLinkCourseId('');
            load();
        } catch (err) {
            toast.error(`Failed: ${err?.response?.data?.detail || err.message}`);
        } finally { setCreating(false); }
    };

    const onDelete = async (id) => {
        if (!window.confirm('Delete this course and all its lessons? Trainees will lose access.')) return;
        try {
            await api.delete(`/admin/videos/${id}`);
            toast.success('Deleted');
            load();
        } catch { toast.error('Delete failed'); }
    };

    const onSaveEdit = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const update = {
            title: fd.get('title'),
            description: fd.get('description'),
            category: fd.get('category') || 'General',
            region: fd.get('region') || null,
            zone: fd.get('zone') || null,
            market: fd.get('market') || null,
            tags: String(fd.get('tags') || '').split(',').map(s => s.trim()).filter(Boolean),
            personas: editPersonas.length ? editPersonas : ['all'],
            language: fd.get('language'),
            mandatory: fd.get('mandatory') === 'on',
            points_on_complete: parseInt(fd.get('points_on_complete'), 10) || 0,
            is_published: fd.get('is_published') === 'on',
            quiz_pass_percent: Math.min(100, Math.max(0, parseInt(fd.get('quiz_pass_percent'), 10) || 70)),
            quiz_question_count: Math.max(0, parseInt(fd.get('quiz_question_count'), 10) || 0),
        };
        if (editLinkId && editLinkId !== (editing.course_code || '')) {
            update.link_course_id = editLinkId === '__new__' ? '__new__' : editLinkId;
        }
        try {
            await api.patch(`/admin/videos/${editing.id}`, update);
            toast.success('Updated');
            setEditing(null);
            setEditLinkId('');
            load();
        } catch { toast.error('Update failed'); }
    };

    // Upload/replace the course thumbnail from the Edit modal (multipart, cache-busted server-side).
    const onEditThumb = async (file) => {
        if (!file || !editing) return;
        if (file.size > 8 * 1024 * 1024) { toast.error('Image too large (max 8MB)'); return; }
        setThumbBusy(true);
        try {
            const fd = new FormData();
            fd.append('thumbnail', file);
            const r = await api.post(`/admin/videos/${editing.id}/thumbnail`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setEditing(r.data);
            setVideos((prev) => prev.map((v) => (v.id === r.data.id ? r.data : v)));
            toast.success('Thumbnail updated');
        } catch (err) {
            toast.error(`Thumbnail upload failed: ${err?.response?.data?.detail || err.message}`);
        } finally { setThumbBusy(false); }
    };

    // Keep the open lessons modal in sync after add/delete
    const syncCourse = (updated) => {
        setLessonsCourse(updated);
        setVideos((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
    };

    // Bulk multilingual course import (.xlsx / .csv in the standard template).
    const onImport = async (file) => {
        if (!file) return;
        setImportBusy(true); setImportResult(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post('/admin/videos/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setImportResult(r.data);
            toast.success(`Imported ${r.data.created} course(s)`);
            load();
        } catch (err) {
            toast.error(`Import failed: ${err?.response?.data?.detail || err.message}`);
        } finally { setImportBusy(false); }
    };
    const downloadTemplate = () => {
        const headers = [
            'Course Title (Text Field)', 'कोर्स टाइटल (टेक्स्ट फील्ड)', 'Language',
            'Duration (in Seconds)(Tentative)', 'Course Category (Dropdown)', 'कोर्स कैटेगरी (ड्रॉपडाउन)',
            'Keywords/ tags (comma separated values associated to course)',
            'कीवर्ड/टैग (कोर्स से संबंधित शब्द, जिन्हें कॉमा से अलग किया गया है)',
            'Region (Dropdown)', 'Zone (Dropdown)', 'Market (Dropdown)', 'Description', 'विवरण',
            'Course ID (optional — same value links language versions; blank = new course)',
        ];
        const csv = '\ufeff' + headers.map((h) => `"${h}"`).join(',') + '\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob); a.download = 'course_import_template.csv'; a.click();
    };

    const filteredVideos = videos.filter((v) => {
        if (flt.q && !(v.title || '').toLowerCase().includes(flt.q.toLowerCase())) return false;
        if (flt.category && v.category !== flt.category) return false;
        if (flt.language && (v.language || 'en') !== flt.language) return false;
        if (flt.region && (v.region || '') !== flt.region) return false;
        if (flt.zone && (v.zone || '') !== flt.zone) return false;
        if (flt.market && (v.market || '') !== flt.market) return false;
        if (flt.persona && !(v.personas || []).includes(flt.persona)) return false;
        if (flt.published === 'yes' && !v.is_published) return false;
        if (flt.published === 'no' && v.is_published) return false;
        return true;
    });

    const codeCounts = {};
    filteredVideos.forEach((v) => { const c = v.course_code || v.id; codeCounts[c] = (codeCounts[c] || 0) + 1; });
    const displayVideos = groupView
        ? [...filteredVideos].sort((a, b) => (a.course_code || '').localeCompare(b.course_code || '') || (a.title || '').localeCompare(b.title || ''))
        : filteredVideos;

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="font-display font-extrabold text-2xl text-slate-900">Courses</h1>
                    <p className="text-sm text-slate-500">Add a course (metadata), then attach one or more lessons — each lesson is an uploaded video or a web/YouTube link.</p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setLinkWizard(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white ring-1 ring-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-50 transition"
                        data-testid="link-wizard-btn">
                        <LinkIcon className="w-4 h-4" /> Link versions
                    </button>
                    <button
                        type="button"
                        onClick={() => { setImportResult(null); setImportOpen(true); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition"
                        data-testid="admin-import-btn">
                        <Upload className="w-4 h-4" /> Import courses
                    </button>
                </div>
            </div>

            {/* PART 1 — Add Course */}
            <form onSubmit={onCreateCourse}
                className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5 space-y-3"
                data-testid="admin-course-create-form">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-600">
                    <span className="grid place-items-center w-5 h-5 rounded-full bg-amber-100 text-amber-700">1</span> Add Course
                </div>

                {/* Course ID (doc Point 20) — new course vs. another language of an existing one */}
                <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 space-y-2" data-testid="course-id-choice">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Course ID</div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <label className={`flex-1 flex items-start gap-2 p-2.5 rounded-lg ring-1 cursor-pointer transition ${courseMode === 'new' ? 'ring-amber-400 bg-amber-50' : 'ring-slate-200 bg-white'}`} data-testid="course-mode-new">
                            <input type="radio" name="course_mode" checked={courseMode === 'new'} onChange={() => { setCourseMode('new'); setLinkCourseId(''); }} className="mt-0.5" />
                            <span>
                                <span className="block text-sm font-bold text-slate-800">New course</span>
                                <span className="block text-[11px] text-slate-500">A fresh Course ID is generated automatically.</span>
                            </span>
                        </label>
                        <label className={`flex-1 flex items-start gap-2 p-2.5 rounded-lg ring-1 cursor-pointer transition ${courseMode === 'link' ? 'ring-amber-400 bg-amber-50' : 'ring-slate-200 bg-white'}`} data-testid="course-mode-link">
                            <input type="radio" name="course_mode" checked={courseMode === 'link'} onChange={() => setCourseMode('link')} className="mt-0.5" />
                            <span>
                                <span className="block text-sm font-bold text-slate-800">Another language of an existing course</span>
                                <span className="block text-[11px] text-slate-500">Shares that course's ID — points are earned only once across languages.</span>
                            </span>
                        </label>
                    </div>
                    {courseMode === 'link' && (
                        <CoursePicker courses={videos} value={linkCourseId} onChange={setLinkCourseId} testid="link-course-picker" placeholder="Search & pick the course this is a language version of…" />
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input name="title" required placeholder="Course title" className={inputCls} data-testid="vu-title" />
                    <select name="language" defaultValue="en" className={inputCls} data-testid="vu-language">
                        {LANGS.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                    </select>
                    <input name="duration_sec" type="number" min="0" placeholder="Duration (sec, optional)" className={inputCls} data-testid="vu-duration" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <CategorySelect name="category" options={tax.category || []} value={createCategory} onChange={setCreateCategory} onAdd={addCategory} onRemove={removeCategory} testid="vu-category" />
                    <input name="tags" placeholder="Keywords / tags (comma separated)" className={inputCls} data-testid="vu-tags" />
                    <select name="region" defaultValue="" className={inputCls} data-testid="vu-region">
                        <option value="">Region (optional)</option>
                        {(tax.region || []).map(o => <option key={o.id} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <select name="zone" defaultValue="" className={inputCls} data-testid="vu-zone">
                        <option value="">Zone (optional)</option>
                        {(tax.zone || []).map(o => <option key={o.id} value={o.value}>{o.label}</option>)}
                    </select>
                    <select name="market" defaultValue="" className={inputCls} data-testid="vu-market">
                        <option value="">Market (optional)</option>
                        {(tax.market || []).map(o => <option key={o.id} value={o.value}>{o.label}</option>)}
                    </select>
                    <div />
                </div>
                <div>
                    <div className="text-xs font-bold text-slate-500 mb-1.5">Personas (who sees this course)</div>
                    <PersonaPicker options={(tax.persona || []).map(o => o.value)} value={createPersonas} onChange={setCreatePersonas} onAdd={addPersona} testid="vu-personas" />
                </div>
                <textarea name="description" placeholder="Description (shown in the player)" rows="2" className={inputCls} data-testid="vu-description" />
                <div className="flex flex-wrap items-center gap-4 text-sm">
                    <label className="inline-flex flex-col gap-1 text-slate-700">
                        <span className="inline-flex items-center gap-2">Thumbnail image (optional)
                            <input type="file" name="thumbnail" accept="image/png,image/jpeg,image/webp" className="text-xs" data-testid="vu-thumbnail" />
                        </span>
                        <span className="text-[11px] text-slate-400 font-medium">Recommended 1080×1080 (square) or 1080×1350 (portrait). The full image is shown in the feed without cropping.</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5 text-slate-700">
                        <input type="checkbox" name="mandatory" data-testid="vu-mandatory" /> Non-skippable first watch
                    </label>
                    <label className="inline-flex items-center gap-1.5 text-slate-700">
                        Points on complete <input type="number" name="points_on_complete" defaultValue="50" min="0" className="w-20 px-2 py-1 rounded-md ring-1 ring-slate-300" data-testid="vu-points" />
                    </label>
                </div>
                <div>
                    <button type="submit" disabled={creating}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm disabled:opacity-50"
                        data-testid="vu-submit">
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {creating ? 'Adding…' : 'Add Course'}
                    </button>
                    <span className="ml-3 text-[11px] text-slate-400">No video here — add lesson videos in Part 2 (the “Lessons” button below).</span>
                </div>
            </form>

            {/* Course field filters (doc Point 25) */}
            <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-3" data-testid="courses-filter-bar">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    <Filter className="w-3.5 h-3.5" /> Filter courses
                </div>
                <div className="flex flex-wrap gap-2">
                    <div className="relative flex-1 min-w-[180px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} placeholder="Search by title…"
                            className="w-full pl-8 pr-3 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="courses-filter-q" />
                    </div>
                    <select value={flt.category} onChange={(e) => setFlt({ ...flt, category: e.target.value })} className="px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="courses-filter-category">
                        <option value="">All categories</option>
                        {(tax.category || []).map((o) => <option key={o.id} value={o.value}>{o.label}</option>)}
                    </select>
                    <select value={flt.language} onChange={(e) => setFlt({ ...flt, language: e.target.value })} className="px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="courses-filter-language">
                        <option value="">All languages</option>
                        {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                    </select>
                    <select value={flt.region} onChange={(e) => setFlt({ ...flt, region: e.target.value })} className="px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="courses-filter-region">
                        <option value="">All regions</option>
                        {(tax.region || []).map((o) => <option key={o.id} value={o.value}>{o.label}</option>)}
                    </select>
                    <select value={flt.zone} onChange={(e) => setFlt({ ...flt, zone: e.target.value })} className="px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="courses-filter-zone">
                        <option value="">All zones</option>
                        {(tax.zone || []).map((o) => <option key={o.id} value={o.value}>{o.label}</option>)}
                    </select>
                    <select value={flt.market} onChange={(e) => setFlt({ ...flt, market: e.target.value })} className="px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="courses-filter-market">
                        <option value="">All markets</option>
                        {(tax.market || []).map((o) => <option key={o.id} value={o.value}>{o.label}</option>)}
                    </select>
                    <select value={flt.persona} onChange={(e) => setFlt({ ...flt, persona: e.target.value })} className="px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="courses-filter-persona">
                        <option value="">All personas</option>
                        {(tax.persona || []).map((o) => <option key={o.id} value={o.value}>{o.label}</option>)}
                    </select>
                    <select value={flt.published} onChange={(e) => setFlt({ ...flt, published: e.target.value })} className="px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="courses-filter-published">
                        <option value="">Any status</option>
                        <option value="yes">Published</option>
                        <option value="no">Unpublished</option>
                    </select>
                    <button type="button" onClick={() => setFlt({ q: '', category: '', language: '', region: '', zone: '', market: '', persona: '', published: '' })}
                        className="text-xs text-slate-600 hover:text-slate-900 px-2" data-testid="courses-filter-clear">Clear</button>
                </div>
            </div>

            {/* Listing */}
            <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                    <div className="font-bold text-slate-800">All courses ({filteredVideos.length}{filteredVideos.length !== videos.length ? ` of ${videos.length}` : ''})</div>
                    <div className="flex items-center gap-3">
                        <button type="button" onClick={() => setGroupView((g) => !g)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold ring-1 transition ${groupView ? 'bg-slate-900 text-white ring-slate-900' : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50'}`}
                            data-testid="group-by-course-id-toggle">
                            <LinkIcon className="w-3.5 h-3.5" /> Group by Course ID
                        </button>
                        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                    </div>
                </div>
                <div className="divide-y divide-slate-100">
                    {filteredVideos.length === 0 && !loading && (
                        <div className="px-5 py-10 text-center text-slate-400 text-sm">
                            <VideoIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            {videos.length === 0 ? 'No courses yet. Add one above, then attach lessons.' : 'No courses match the current filters.'}
                        </div>
                    )}
                    {displayVideos.map((v, idx) => {
                        const code = v.course_code || v.id;
                        const showHeader = groupView && (idx === 0 || (displayVideos[idx - 1].course_code || displayVideos[idx - 1].id) !== code);
                        const grpCount = codeCounts[code] || 1;
                        return (
                        <React.Fragment key={v.id}>
                            {showHeader && (
                                <div className={`px-5 py-2 flex items-center gap-2 text-[11px] font-mono ${grpCount > 1 ? 'bg-amber-50 text-amber-800' : 'bg-slate-50 text-slate-500'}`} data-testid={`course-group-header-${code}`}>
                                    <LinkIcon className="w-3 h-3" /> <span>{code}</span>
                                    <span className="font-sans font-bold">· {grpCount} {grpCount > 1 ? 'language versions (points once)' : 'course'}</span>
                                </div>
                            )}
                        <div className="px-5 py-3 flex items-center gap-4" data-testid={`admin-video-row-${v.id}`}>
                            <div className="flex-shrink-0 w-16 h-12 bg-slate-800 rounded-lg overflow-hidden grid place-items-center text-slate-400">
                                {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <VideoIcon className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-800 text-sm truncate">{v.title}</div>
                                <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                                    <span className="uppercase font-bold">{v.language}</span>
                                    <span>· {v.category_label || v.category}</span>
                                    {v.course_code && <span className="font-mono text-[10px] text-slate-400" title="Course ID (shared across language versions)" data-testid={`course-code-${v.id}`}>· {v.course_code}</span>}
                                    {(v.lesson_count || 0) > 0
                                        ? <span className="text-emerald-600 font-bold">· {v.lesson_count} lesson{v.lesson_count === 1 ? '' : 's'}</span>
                                        : <span className="text-amber-600 font-bold">· No video yet</span>}
                                    {v.mandatory && <span className="text-rose-600 font-bold">· Mandatory</span>}
                                </div>
                            </div>
                            <div className="text-xs text-slate-500 hidden sm:flex items-center gap-3">
                                <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {v.view_count || 0}</span>
                                <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {v.like_count || 0}</span>
                                <span className="inline-flex items-center gap-1"><Star className="w-3 h-3" /> {(v.rating_count ? (v.rating_sum / v.rating_count).toFixed(1) : '—')}</span>
                            </div>
                            <button onClick={() => setLessonsCourse(v)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-700" data-testid={`admin-course-lessons-${v.id}`}>
                                <ListVideo className="w-3.5 h-3.5" /> Lessons ({v.lesson_count || 0})
                            </button>
                            <button onClick={() => { setEditing(v); setEditPersonas(v.personas || ['all']); setEditCategory(v.category || (tax.category || [])[0]?.value || ''); setEditLinkId(''); }} className="p-1.5 rounded-lg hover:bg-slate-100" title="Edit" data-testid={`admin-video-edit-${v.id}`}>
                                <Edit3 className="w-4 h-4 text-slate-600" />
                            </button>
                            <button onClick={() => onDelete(v.id)} className="p-1.5 rounded-lg hover:bg-rose-50" title="Delete" data-testid={`admin-video-del-${v.id}`}>
                                <Trash2 className="w-4 h-4 text-rose-600" />
                            </button>
                        </div>
                        </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* Edit course modal (metadata) */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEditing(null)}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={onSaveEdit}
                        className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 ring-1 ring-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto"
                        data-testid="admin-video-edit-modal">
                        <h3 className="font-display font-bold text-lg text-slate-900">Edit course</h3>
                        <div className="flex items-center gap-3 rounded-xl ring-1 ring-slate-200 p-2.5" data-testid="edit-thumbnail-block">
                            <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 grid place-items-center flex-shrink-0">
                                {editing.thumbnail_url
                                    ? <img src={editing.thumbnail_url} alt="thumbnail" className="w-full h-full object-cover" data-testid="edit-thumbnail-preview" />
                                    : <VideoIcon className="w-6 h-6 text-slate-400" />}
                            </div>
                            <div className="min-w-0">
                                <div className="text-xs font-bold text-slate-600 mb-1">Course thumbnail</div>
                                <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${thumbBusy ? 'bg-slate-100 text-slate-400 cursor-wait' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer'}`} data-testid="edit-thumbnail-upload">
                                    {thumbBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                    {editing.thumbnail_url && !/default/i.test(editing.thumbnail_url) ? 'Replace image' : 'Upload image'}
                                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={thumbBusy}
                                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onEditThumb(f); }} />
                                </label>
                                <p className="text-[11px] text-slate-400 mt-1">JPG/PNG/WebP · 1080×1080 or 1080×1350 · max 8MB</p>
                            </div>
                        </div>
                        <input name="title" defaultValue={editing.title} required className={inputCls} />
                        <CategorySelect name="category" options={tax.category || []} value={editCategory} onChange={setEditCategory} onAdd={addCategory} onRemove={removeCategory} testid="edit-category" />
                        <textarea name="description" defaultValue={editing.description} rows="2" className={inputCls} />
                        <input name="tags" defaultValue={(editing.tags || []).join(', ')} placeholder="tags (comma separated)" className={inputCls} />
                        <div className="grid grid-cols-3 gap-2">
                            <select name="region" defaultValue={editing.region || ''} className={inputCls}>
                                <option value="">Region</option>
                                {(tax.region || []).map(o => <option key={o.id} value={o.value}>{o.label}</option>)}
                            </select>
                            <select name="zone" defaultValue={editing.zone || ''} className={inputCls}>
                                <option value="">Zone</option>
                                {(tax.zone || []).map(o => <option key={o.id} value={o.value}>{o.label}</option>)}
                            </select>
                            <select name="market" defaultValue={editing.market || ''} className={inputCls}>
                                <option value="">Market</option>
                                {(tax.market || []).map(o => <option key={o.id} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="text-xs font-bold text-slate-500 mb-1.5">Personas</div>
                            <PersonaPicker options={(tax.persona || []).map(o => o.value)} value={editPersonas} onChange={setEditPersonas} onAdd={addPersona} testid="edit-personas" />
                        </div>
                        <select name="language" defaultValue={editing.language} className={inputCls}>
                            {LANGS.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                        </select>
                        <div className="flex flex-wrap gap-4 text-sm">
                            <label className="inline-flex items-center gap-1.5"><input type="checkbox" name="mandatory" defaultChecked={editing.mandatory} /> Mandatory</label>
                            <label className="inline-flex items-center gap-1.5"><input type="checkbox" name="is_published" defaultChecked={editing.is_published} /> Published</label>
                            <label className="inline-flex items-center gap-1.5">Points<input type="number" name="points_on_complete" defaultValue={editing.points_on_complete} className="w-16 px-2 py-1 rounded-md ring-1 ring-slate-300" /></label>
                        </div>
                        <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 space-y-2" data-testid="edit-course-id-block">
                            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Course ID (links language versions)</div>
                            <div className="flex items-center gap-2 flex-wrap text-sm">
                                <span className="px-2 py-1 rounded-lg bg-white ring-1 ring-slate-300 font-mono text-xs text-slate-700" data-testid="edit-current-course-id">
                                    {editLinkId === '__new__' ? 'New ID on save' : (editLinkId ? 'Will link on save' : (editing.course_code || '—'))}
                                </span>
                                <button type="button" onClick={() => setEditLinkId('__new__')} className="text-xs font-bold text-slate-600 hover:text-slate-900 underline" data-testid="edit-unlink-btn">Make standalone (new ID)</button>
                                {editLinkId && <button type="button" onClick={() => setEditLinkId('')} className="text-xs text-slate-500 hover:text-slate-800" data-testid="edit-link-reset">reset</button>}
                            </div>
                            <div className="text-[11px] text-slate-500">To mark this as another language of an existing course, pick it below — this course will share that course's ID (points once).</div>
                            <CoursePicker courses={videos} value={editLinkId === '__new__' ? '' : editLinkId} onChange={setEditLinkId} excludeId={editing.id} testid="edit-link-course-picker" placeholder="Group with an existing course…" />
                        </div>
                        <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 p-3 space-y-2" data-testid="quiz-config-block">
                            <div className="text-xs font-bold uppercase tracking-wider text-amber-700">Quiz &amp; Certification rules</div>
                            <div className="flex flex-wrap gap-4 text-sm">
                                <label className="inline-flex items-center gap-1.5">Pass mark %<input type="number" name="quiz_pass_percent" min="0" max="100" defaultValue={editing.quiz_pass_percent ?? 70} className="w-20 px-2 py-1 rounded-md ring-1 ring-slate-300" data-testid="vu-pass-percent" /></label>
                                <label className="inline-flex items-center gap-1.5">Questions / attempt<input type="number" name="quiz_question_count" min="0" defaultValue={editing.quiz_question_count ?? 0} className="w-20 px-2 py-1 rounded-md ring-1 ring-slate-300" data-testid="vu-question-count" /></label>
                            </div>
                            <p className="text-[11px] text-amber-600">Set <b>Questions / attempt</b> to 0 to use the entire question bank each time. Trainees get unlimited retakes; passing issues the certificate &amp; awards points.</p>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm">Cancel</button>
                            <button type="submit" className="px-4 py-2 rounded-xl bg-amber-500 text-slate-900 font-bold text-sm" data-testid="admin-video-save-btn">Save</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Bulk multilingual course import */}
            {importOpen && (
                <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm grid place-items-center p-4" onClick={() => setImportOpen(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 ring-1 ring-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-import-modal">
                        <h3 className="font-display font-bold text-lg text-slate-900">Import courses (multilingual)</h3>
                        <p className="text-sm text-slate-500">Upload an <b>.xlsx</b> or <b>.csv</b> in the standard template. Each row creates a course in the row's <b>Language</b>, using the translated Title / Category / Keywords / Description. Duration, Region, Zone and Market are imported as-is. To link language versions of the SAME course (so points are earned once), give them the <b>same Course ID</b> in the last column — leave it blank for a standalone course. Courses are created as metadata — add lessons afterwards from each course's Lessons manager.</p>
                        <button type="button" onClick={downloadTemplate} className="text-xs font-bold text-amber-600 underline" data-testid="import-download-template">Download CSV template</button>
                        <label className={`flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed text-sm font-bold ${importBusy ? 'border-slate-200 text-slate-400 cursor-wait' : 'border-amber-300 text-amber-700 hover:bg-amber-50 cursor-pointer'}`} data-testid="import-dropzone">
                            {importBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            {importBusy ? 'Importing…' : 'Choose .xlsx / .csv file'}
                            <input type="file" accept=".xlsx,.csv" className="hidden" disabled={importBusy}
                                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onImport(f); }} data-testid="import-file-input" />
                        </label>
                        {importResult && (
                            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-sm space-y-1" data-testid="import-result">
                                <div className="font-bold text-emerald-700">Created: {importResult.created}</div>
                                {importResult.skipped > 0 && <div className="text-rose-600 font-bold">Skipped: {importResult.skipped}</div>}
                                {(importResult.errors || []).slice(0, 8).map((er, i) => <div key={i} className="text-xs text-slate-500">{er}</div>)}
                            </div>
                        )}
                        <div className="flex justify-end">
                            <button type="button" onClick={() => setImportOpen(false)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm" data-testid="import-close-btn">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* PART 2 — Lessons manager modal */}
            {lessonsCourse && (
                <LessonsModal course={lessonsCourse} onClose={() => setLessonsCourse(null)} onChange={syncCourse} />
            )}
            {linkWizard && (
                <LinkWizard courses={videos} onClose={() => setLinkWizard(false)} onDone={() => { setLinkWizard(false); load(); }} />
            )}
        </div>
    );
}

// Retro-link wizard (Point 20): bulk-tag existing language versions with one shared Course ID.
function LinkWizard({ courses, onClose, onDone }) {
    const [query, setQuery] = useState('');
    const [cat, setCat] = useState('');
    const [selected, setSelected] = useState([]);   // course ids
    const [target, setTarget] = useState('new');     // 'new' | course id whose code to reuse
    const [busy, setBusy] = useState(false);

    const cats = [...new Map(courses.map((c) => [c.category, c.category_label || c.category])).entries()].map(([v, l]) => ({ v, l }));
    const q = query.trim().toLowerCase();
    const list = courses
        .filter((c) => (!cat || c.category === cat) && (!q || (c.title || '').toLowerCase().includes(q) || (c.course_code || '').toLowerCase().includes(q)))
        .sort((a, b) => (a.category || '').localeCompare(b.category || '') || (a.title || '').localeCompare(b.title || ''));
    const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const submit = async () => {
        if (selected.length < 2) { toast.error('Select at least 2 courses to link as one'); return; }
        setBusy(true);
        try {
            const body = { course_ids: selected };
            if (target !== 'new') body.link_to = target;
            const r = await api.post('/admin/courses/link-group', body);
            toast.success(`Linked ${r.data.updated} course(s) under Course ID ${r.data.course_code}`);
            onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Link failed'); }
        finally { setBusy(false); }
    };

    const selectedCourses = courses.filter((c) => selected.includes(c.id));

    return (
        <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-2xl p-5 space-y-3 ring-1 ring-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="link-wizard-modal">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-display font-bold text-lg text-slate-900">Link language versions</h3>
                        <p className="text-xs text-slate-500">Pick the courses that are the SAME course in different languages — they'll share one Course ID so points are earned only once. Tip: filter by category to find versions fast.</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
                </div>

                <div className="flex flex-wrap gap-2">
                    <div className="relative flex-1 min-w-[160px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search courses…"
                            className="w-full pl-8 pr-3 py-2 rounded-lg ring-1 ring-slate-300 text-sm" data-testid="link-wizard-search" />
                    </div>
                    <select value={cat} onChange={(e) => setCat(e.target.value)} className="px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="link-wizard-category">
                        <option value="">All categories</option>
                        {cats.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                    </select>
                </div>

                <div className="max-h-64 overflow-y-auto rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100" data-testid="link-wizard-list">
                    {list.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-400">No courses match.</div>}
                    {list.map((c) => (
                        <label key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`link-wizard-item-${c.id}`}>
                            <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
                            <span className="flex-1 min-w-0 truncate text-slate-800">{c.title}</span>
                            <span className="text-[10px] uppercase font-bold text-slate-500">{c.language}</span>
                            <span className="font-mono text-[10px] text-slate-400">{c.course_code}</span>
                        </label>
                    ))}
                </div>

                {selected.length > 0 && (
                    <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 space-y-2">
                        <div className="text-xs font-bold text-slate-600">{selected.length} selected → shared Course ID</div>
                        <select value={target} onChange={(e) => setTarget(e.target.value)} className="w-full px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="link-wizard-target">
                            <option value="new">Create a NEW shared Course ID</option>
                            {selectedCourses.map((c) => <option key={c.id} value={c.id}>{`Use "${c.title}" (${(c.language || 'en').toUpperCase()}) → ${c.course_code}`}</option>)}
                        </select>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    <button onClick={onClose} className="px-3 py-2 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100" data-testid="link-wizard-cancel">Cancel</button>
                    <button onClick={submit} disabled={busy || selected.length < 2}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="link-wizard-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />} Link as one course
                    </button>
                </div>
            </div>
        </div>
    );
}

function CoursePicker({ courses, value, onChange, testid, placeholder, excludeId }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const boxRef = useRef(null);
    const list = (courses || []).filter((c) => c.id !== excludeId);
    const selected = list.find((c) => c.id === value);
    const q = query.trim().toLowerCase();
    const filtered = q ? list.filter((c) => (c.title || '').toLowerCase().includes(q) || (c.course_code || '').toLowerCase().includes(q)) : list;
    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);
    return (
        <div ref={boxRef} className="relative w-full" data-testid={testid}>
            <button type="button" onClick={() => { setOpen((o) => !o); setQuery(''); }}
                className="w-full flex items-center justify-between text-left px-3 py-2.5 rounded-xl ring-1 ring-slate-300 bg-white text-sm" data-testid={`${testid}-trigger`}>
                <span className={`truncate ${selected ? 'text-slate-800' : 'text-slate-400'}`}>
                    {selected ? `${selected.title} · ${(selected.language || 'en').toUpperCase()} · ${selected.course_code || ''}` : (placeholder || '— Pick a course —')}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute z-40 mt-1 w-full bg-white rounded-xl ring-1 ring-slate-200 shadow-xl overflow-hidden">
                    <div className="p-2 border-b border-slate-100">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search courses…"
                                className="w-full pl-8 pr-3 py-2 rounded-lg ring-1 ring-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid={`${testid}-search`} />
                        </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto py-1">
                        {filtered.length === 0 && <div className="px-3 py-4 text-center text-sm text-slate-400">No courses match “{query}”</div>}
                        {filtered.map((c) => (
                            <button key={c.id} type="button" onClick={() => { onChange(c.id); setOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-amber-50 ${c.id === value ? 'bg-amber-50' : ''}`}
                                data-testid={`${testid}-option-${c.id}`}>
                                <span className="truncate text-slate-700">{c.title}</span>
                                <span className="text-[10px] text-slate-400 shrink-0 inline-flex items-center gap-1">
                                    {(c.language || 'en').toUpperCase()} · {c.course_code} {c.id === value && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function LessonsModal({ course, onClose, onChange }) {
    const [mode, setMode] = useState('url');   // 'url' | 'upload'
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const lessons = course.lessons || [];

    const addLesson = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const fd = new FormData(form);
        if (!String(fd.get('title') || '').trim()) { toast.error('Lesson title is required'); return; }
        if (mode === 'upload') {
            const f = fd.get('file');
            if (!f || f.size === 0) { toast.error('Choose a video file'); return; }
            if (f.size > 2048 * 1024 * 1024) { toast.error('Video larger than 2GB.'); return; }
            fd.delete('video_url');
        } else {
            if (!String(fd.get('video_url') || '').trim()) { toast.error('Paste a video URL (YouTube or direct link)'); return; }
            fd.delete('file');
        }
        setBusy(true); setProgress(0);
        try {
            if (mode === 'upload') {
                // Chunked upload bypasses proxy body-size limits for large videos.
                const f = fd.get('file');
                const up = await chunkedUpload(f, 'lesson', (p) => setProgress(p));
                fd.delete('file');
                fd.set('upload_id', up.upload_id);
            }
            const r = await api.post(`/admin/videos/${course.id}/lessons`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Lesson added');
            form.reset();
            setProgress(0);
            onChange(r.data);
        } catch (err) {
            toast.error(`Failed: ${err?.response?.data?.detail || err.message}`);
        } finally { setBusy(false); }
    };

    const delLesson = async (lid) => {
        if (!window.confirm('Remove this lesson?')) return;
        try {
            const r = await api.delete(`/admin/videos/${course.id}/lessons/${lid}`);
            toast.success('Lesson removed');
            onChange(r.data);
        } catch { toast.error('Failed to remove'); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl w-full max-w-2xl ring-1 ring-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto"
                data-testid="admin-lessons-modal">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-amber-600 flex items-center gap-2">
                            <span className="grid place-items-center w-5 h-5 rounded-full bg-amber-100 text-amber-700">2</span> Lessons
                        </div>
                        <h3 className="font-display font-bold text-lg text-slate-900">{course.title}</h3>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
                </div>

                {/* Existing lessons */}
                <div className="p-5 space-y-2">
                    {lessons.length === 0 && (
                        <div className="text-center text-slate-400 text-sm py-6">No lessons yet. Add the first one below.</div>
                    )}
                    {lessons.map((l, i) => (
                        <div key={l.id} className="flex items-center gap-3 p-3 rounded-xl ring-1 ring-slate-200" data-testid={`lesson-row-${l.id}`}>
                            <div className="w-7 h-7 rounded-full bg-slate-100 grid place-items-center text-xs font-bold text-slate-500">{i + 1}</div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-800 text-sm truncate">{l.title}</div>
                                <div className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5">
                                    {l.kind === 'youtube' ? <><Youtube className="w-3 h-3 text-rose-500" /> YouTube</>
                                        : l.kind === 'url' ? <><LinkIcon className="w-3 h-3 text-sky-500" /> Web URL</>
                                            : <><Upload className="w-3 h-3 text-emerald-500" /> Uploaded</>}
                                    {l.video_url && <span className="truncate max-w-[260px]"> · {l.video_url}</span>}
                                </div>
                            </div>
                            <button onClick={() => delLesson(l.id)} className="p-1.5 rounded-lg hover:bg-rose-50" data-testid={`lesson-del-${l.id}`}>
                                <Trash2 className="w-4 h-4 text-rose-600" />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Add lesson */}
                <form onSubmit={addLesson} className="px-5 pb-5 space-y-3 border-t border-slate-100 pt-4" data-testid="add-lesson-form">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add a lesson</div>
                    <input name="title" placeholder="Lesson title" className={inputCls} data-testid="lesson-title" />
                    <div className="inline-flex rounded-xl ring-1 ring-slate-200 p-1 bg-slate-50 text-sm">
                        <button type="button" onClick={() => setMode('url')} className={`px-3 py-1.5 rounded-lg font-bold ${mode === 'url' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`} data-testid="lesson-mode-url">Video URL (YouTube / web)</button>
                        <button type="button" onClick={() => setMode('upload')} className={`px-3 py-1.5 rounded-lg font-bold ${mode === 'upload' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`} data-testid="lesson-mode-upload">Upload file</button>
                    </div>
                    {mode === 'url' ? (
                        <input name="video_url" placeholder="https://youtube.com/watch?v=…  or  https://…/video.mp4" className={inputCls} data-testid="lesson-url" />
                    ) : (
                        <input type="file" name="file" accept="video/mp4,video/webm,video/quicktime,video/x-matroska" className="text-sm" data-testid="lesson-file" />
                    )}
                    <div className="flex items-center gap-3">
                        <input name="duration_sec" type="number" min="0" placeholder="Duration sec (optional)" className="w-44 px-3 py-2 rounded-lg ring-1 ring-slate-300 text-sm" data-testid="lesson-duration" />
                        <button type="submit" disabled={busy}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-700 disabled:opacity-50"
                            data-testid="lesson-add-btn">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            {busy ? (mode === 'upload' ? `Uploading ${progress}%` : 'Adding…') : 'Add lesson'}
                        </button>
                    </div>
                    <div className="text-[11px] text-slate-400">Uploads: Max 2GB · MP4/WebM/MOV/MKV. URLs: YouTube links auto-embed; direct MP4 links play inline.</div>
                </form>
            </div>
        </div>
    );
}

function CategorySelect({ name, options, value, onChange, onAdd, onRemove, testid }) {
    const [adding, setAdding] = useState(false);
    const [managing, setManaging] = useState(false);
    const [confirmId, setConfirmId] = useState(null);
    const [text, setText] = useState('');
    const opts = options || [];
    const hasValue = value && opts.some((o) => o.value === value);
    const submitAdd = async () => {
        const label = text.trim();
        if (!label) { setAdding(false); return; }
        const slug = onAdd ? await onAdd(label) : label.toLowerCase().replace(/\s+/g, '_');
        onChange(slug);
        setText(''); setAdding(false);
    };
    return (
        <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
                <select name={name} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
                    {opts.map((o) => <option key={o.id} value={o.value}>{o.label}</option>)}
                    {!hasValue && value && <option value={value}>{value}</option>}
                </select>
                {onAdd && !adding && (
                    <button type="button" onClick={() => { setAdding(true); setManaging(false); }} data-testid={`${testid}-add-btn`}
                        className="flex-shrink-0 px-2.5 py-2 rounded-lg text-xs font-bold border border-dashed border-slate-300 text-slate-500 hover:border-amber-400 hover:text-amber-600 whitespace-nowrap">
                        + Add
                    </button>
                )}
                {onRemove && opts.length > 0 && !adding && (
                    <button type="button" onClick={() => { setManaging((m) => !m); setConfirmId(null); }} title="Manage / remove categories" data-testid={`${testid}-manage-btn`}
                        className={`flex-shrink-0 px-2 py-2 rounded-lg border border-dashed ${managing ? 'border-rose-400 text-rose-600 bg-rose-50' : 'border-slate-300 text-slate-500'} hover:border-rose-400 hover:text-rose-600`}>
                        <Settings2 className="w-4 h-4" />
                    </button>
                )}
            </div>
            {onAdd && adding && (
                <div className="flex items-center gap-1.5">
                    <input autoFocus value={text} onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitAdd(); } if (e.key === 'Escape') { setAdding(false); setText(''); } }}
                        placeholder="New category name" data-testid={`${testid}-add-input`}
                        className="flex-1 px-3 py-2 rounded-lg text-sm ring-1 ring-amber-400 outline-none" />
                    <button type="button" onClick={submitAdd} data-testid={`${testid}-add-save`}
                        className="px-3 py-2 rounded-lg text-xs font-bold bg-amber-500 text-slate-900">Save</button>
                    <button type="button" onClick={() => { setAdding(false); setText(''); }}
                        className="px-2 py-2 rounded-lg text-xs font-bold text-slate-400 hover:text-slate-600">Cancel</button>
                </div>
            )}
            {onRemove && managing && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 space-y-1 max-h-44 overflow-y-auto" data-testid={`${testid}-manage-panel`}>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 px-1 pb-0.5">Remove a category</div>
                    {opts.length === 0 && <div className="text-xs text-slate-400 px-1 py-1">No categories yet.</div>}
                    {opts.map((o) => (
                        <div key={o.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-white ring-1 ring-slate-100">
                            <span className="truncate text-sm text-slate-700">{o.label}</span>
                            {confirmId === o.id ? (
                                <span className="flex items-center gap-1 flex-shrink-0">
                                    <button type="button" onClick={() => { setConfirmId(null); onRemove(o); }} data-testid={`${testid}-remove-confirm-${o.value}`}
                                        className="px-2 py-0.5 rounded text-[11px] font-bold bg-rose-600 text-white">Remove</button>
                                    <button type="button" onClick={() => setConfirmId(null)}
                                        className="px-1.5 py-0.5 rounded text-[11px] font-bold text-slate-400">Cancel</button>
                                </span>
                            ) : (
                                <button type="button" onClick={() => setConfirmId(o.id)} title={`Remove ${o.label}`} data-testid={`${testid}-remove-${o.value}`}
                                    className="flex-shrink-0 p-1 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function PersonaPicker({ options, value, onChange, testid, onAdd }) {
    const [adding, setAdding] = useState(false);
    const [text, setText] = useState('');
    const all = ['all', ...(options || []).filter((o) => o !== 'all')];
    const toggle = (p) => {
        if (p === 'all') { onChange(['all']); return; }
        let next = (value || []).filter((x) => x !== 'all');
        next = next.includes(p) ? next.filter((x) => x !== p) : [...next, p];
        onChange(next.length ? next : ['all']);
    };
    const submitAdd = async () => {
        const v = text.trim().toLowerCase().replace(/\s+/g, '_');
        if (!v) { setAdding(false); return; }
        if (onAdd) await onAdd(v);
        onChange([...(value || []).filter((x) => x !== 'all'), v]);
        setText(''); setAdding(false);
    };
    return (
        <div className="flex flex-wrap gap-1.5 items-center" data-testid={testid}>
            {all.map((p) => (
                <button type="button" key={p} onClick={() => toggle(p)} data-testid={`persona-opt-${p}`}
                    className={`px-2.5 py-1 rounded-full text-xs font-bold border transition ${(value || []).includes(p) ? 'bg-amber-500 text-slate-900 border-amber-500' : 'bg-white text-slate-500 border-slate-300 hover:border-amber-400'}`}>
                    {p}
                </button>
            ))}
            {onAdd && (adding ? (
                <span className="inline-flex items-center gap-1">
                    <input
                        autoFocus value={text} onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitAdd(); } if (e.key === 'Escape') { setAdding(false); setText(''); } }}
                        placeholder="new persona" data-testid={`${testid}-add-input`}
                        className="px-2 py-1 rounded-full text-xs ring-1 ring-amber-400 outline-none w-28" />
                    <button type="button" onClick={submitAdd} data-testid={`${testid}-add-save`}
                        className="text-xs font-bold text-amber-600 hover:text-amber-700">Save</button>
                </span>
            ) : (
                <button type="button" onClick={() => setAdding(true)} data-testid={`${testid}-add-btn`}
                    className="px-2.5 py-1 rounded-full text-xs font-bold border border-dashed border-slate-300 text-slate-500 hover:border-amber-400 hover:text-amber-600">
                    + Add persona
                </button>
            ))}
        </div>
    );
}
